import React, { useEffect, useState, useMemo } from "react";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { useForm, useWatch } from "react-hook-form";
import { useAuth } from "./../util/auth";
import { makeStyles } from '@material-ui/core/styles';
import { updateProduction, useProductionsByOwner } from "../util/db";
import { US_STATES,COUNTRIES } from "../util/constants";
import PhoneMask from "./PhoneMask";
import { checkIfProductionNameAndCrewOrExtraKeyExists, validateProductionKeys } from "../util/apiHelpers";

const useStyles = makeStyles(() => ({
    root: {
      '& input::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        cursor: 'pointer',
      },
    },

  }));

function SettingsGeneral(props) {
  const auth = useAuth();
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const {
    data: productions,
    status: productionsStatus,
    error: productionsError,
  } = useProductionsByOwner(auth.user.uid);

  const classes = useStyles();

  const productionsAreEmpty = !productions || productions.length === 0;
  const defaultValues = useMemo(() => {
    const prod = productions?.[0];
    const address = prod?.businessAddress || {};
    return {
      businessName: prod?.businessName || "",
      addressLine1: address.addressLine1 || "",
      addressLine2: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      postal_code: address.postal_code || "",
      country: address.country || "",
        productionName: prod?.productionName || "",
      email: auth.user?.email || "",
      phone: prod?.phone || "",
      accessKey: prod?.accessKey?.key || "",
      accessKeyForExtras: prod?.accessKeyForExtras?.key || "",
      accessKeyForExtrasExpiry: prod?.accessKeyForExtras?.expiry || "",
    };
  }, [productions, auth.user?.email]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  // Reset form when Firestore data loads
  useEffect(() => {
    if (productions?.[0]) {
      reset(defaultValues);
    }
  }, [productions, defaultValues, reset]);

  const watchedValues = useWatch({ control });
  const isModified = useMemo(() => {
    const watchedPhone = watchedValues.phone?.replace(/[^0-9]/g, "") || "";
    const defaultPhone = defaultValues.phone?.replace(/[^0-9]/g, "") || "";
    return (
      watchedValues.businessName !== defaultValues.businessName ||
      watchedValues.addressLine1 !== defaultValues.addressLine1 ||
      watchedValues.addressLine2 !== defaultValues.addressLine2 ||
      watchedValues.city !== defaultValues.city ||
      watchedValues.state !== defaultValues.state ||
      watchedValues.postal_code !== defaultValues.postal_code ||
      watchedValues.country !== defaultValues.country ||
      watchedValues.productionName?.toUpperCase() !== defaultValues.productionName?.toUpperCase() ||
      watchedValues.email !== defaultValues.email ||
      watchedValues.accessKey !== defaultValues.accessKey ||
      watchedValues.accessKeyForExtras !== defaultValues.accessKeyForExtras ||
      watchedValues.accessKeyForExtrasExpiry !== defaultValues.accessKeyForExtrasExpiry ||
      watchedPhone !== defaultPhone
    );
  }, [watchedValues, defaultValues]);

  const onSubmit = async (data) => {
    setSubmitError(null);
    setPending(true);
    const productionId = productions[0].id;

        // // Check if productionName + crewKey or productionName + extraKey already exist


        // const crewKeyExists = await checkIfProductionNameAndCrewKeyExists(data.productionName, data.accessKey, productionId);
        // const extraKeyExists = await checkIfProductionNameAndExtraKeyExists(data.productionName, data.accessKeyForExtras, productionId);
    
        // if (crewKeyExists || extraKeyExists) {
        //   const conflictField = crewKeyExists ? "crew key" : "extra key";
        //   setSubmitError({
        //     message: `A production with this name and ${conflictField} already exists. Please choose a different production name or key.`
        //   });
        //   setPending(false);
        //   return; // stop the signup process
        // }
    
        try {
          const result = await checkIfProductionNameAndCrewOrExtraKeyExists(data.productionName, data.accessKey, data.accessKeyForExtras, productionId);
          if (result.exists) {
            const conflictField =
              result.duplicateType === "crewKey"
                ? "crew key"
                : result.duplicateType === "extraKey"
                ? "extra key"
                : "key";

            setSubmitError({
              message: `A production with this name and ${conflictField} already exists. Please choose a different production name or key.`
            });
        
            setPending(false);
            return; // stop the signup process
          } else {
            console.log('No dupes. you can save.');
          }
        
          // continue signup if no duplicate
        } catch (err) {
          console.error("Error checking production duplicate:", err);
          setSubmitError({
            message: `Failed while checking for duplicate production name and key combo.`
          });
          setPending(false);
          return;
        }

    const businessAddress = {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      city: data.city,
      state: data.state,
      postal_code: data.postal_code,
      country: data.country,
    };

    const modifiedData = {
      businessName: data.businessName,
      businessAddress, // <-- nested object
      productionName: data.productionName,
      // email: data.email,
      phone: data.phone,
      accessKey: { type: "crew", key: data.accessKey, expiry: false },
      accessKeyForExtras: {
        type: "extra",
        key: data.accessKeyForExtras,
        expiry: data.accessKeyForExtrasExpiry,
      },
    };
    updateProduction(productionId, modifiedData)
      .then(() =>
        validateProductionKeys(productionId, productions?.[0], modifiedData)
      )
      .then(() =>
        auth.updateProfile({
          businessName: modifiedData.businessName,
          productionName: modifiedData.productionName,
        })
      )
      .then(() => {
        props.onStatus({
          type: "success",
          message: "Your production settings have been updated",
        });
      })
      .catch((error) => {
        if (error.code === "auth/requires-recent-login") {
          props.onStatus({
            type: "requires-recent-login",
            callback: () => onSubmit(modifiedData),
          });
        } else {
          alert(error.message)
          props.onStatus({
            type: "error",
            message: error.message,
          });
        }
      })
      .finally(() => setPending(false));
  };

  const productionNameValue = watch("productionName");
  const accessKeyValue = watch("accessKey");

  return (
    <>
        {!auth.rawUser.emailVerified ? <Box mb={3}>
          <Alert severity="error">Email has not been verified yet.</Alert>
        </Box> : <Box mb={3}>
          <Alert severity="success">Email has been verified.</Alert>
        </Box>}
      {productionsError && (
        <Box mb={3}>
          <Alert severity="error">{productionsError.message}</Alert>
        </Box>
      )}

      {(productionsStatus === "loading" || productionsAreEmpty) && (
        <Box py={5} px={3} align="center">
          {productionsStatus === "loading" && <CircularProgress size={32} />}
          {productionsStatus !== "loading" && productionsAreEmpty && (
            <>No production data available.</>
          )}
        </Box>
      )}

      {productionsStatus !== "loading" && productions && productions.length > 0 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Company Name"
                name="businessName"
                placeholder="Business Name"
                error={!!errors.businessName}
                helperText={errors.businessName?.message}
                fullWidth
                inputRef={register({ required: "Please enter the business name" })}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Company Address"
                name="businessAddress"
                placeholder="Business Address"
                error={!!errors.businessAddress}
                helperText={errors.businessAddress?.message}
                fullWidth
                inputRef={register({ required: "Please enter the business Address" })}
              />
            </Grid> */}

            {/* Address Line 1 */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Address Line 1"
                name="addressLine1"
                placeholder="Street address"
                error={!!errors.addressLine1}
                helperText={errors.addressLine1?.message}
                fullWidth
                inputRef={register({ required: "Address Line 1 is required" })}
              />
            </Grid>

            {/* Address Line 2 */}
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                label="Address Line 2"
                name="addressLine2"
                placeholder="Apartment, suite, etc. (optional)"
                error={!!errors.addressLine2}
                helperText={errors.addressLine2?.message}
                fullWidth
                inputRef={register()} // optional, no required validation
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                label="City"
                name="city"
                placeholder="City"
                error={!!errors.city}
                helperText={errors.city?.message}
                fullWidth
                inputRef={register({ required: "City is required" })}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                select
                label="State"
                name="state"
                error={!!errors.state}
                helperText={errors.state?.message}
                fullWidth
                inputRef={register({ required: "State is required" })}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            {/* Postal Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                label="Postal Code"
                name="postal_code"
                placeholder="Postal Code"
                error={!!errors.postal_code}
                helperText={errors.postal_code?.message}
                fullWidth
                inputRef={register({ required: "Postal Code is required" })}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                select
                label="Country"
                name="country"
                error={!!errors.country}
                helperText={errors.country?.message}
                fullWidth
                inputRef={register({ required: "Country is required" })}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Production Name for App / Map"
                name="productionName"
                placeholder="Production Name"
                error={!!errors.productionName}
                helperText={errors.productionName?.message}
                fullWidth
                inputRef={register({ required: "Please enter the production name" })}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
            </Grid>

            {watchedValues.productionName?.toUpperCase() !== defaultValues.productionName?.toUpperCase() && 
              <Grid item xs={12} 
                style={{color: '#ff4242', border: '1px solid #ff4242', marginLeft: 8, marginRight: 8, 
                marginBottom: 10, borderRadius: '3px'}}>
                  ⚠️ Warning: Changing the Production Name will unjoin mobile users. They’ll need the new Production Name and Passkey 
                  to regain access. Location groups linked to the old name won’t carry over when users join with the new name — copy 
                  and paste any needed groups after renaming.
              </Grid>
            }

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="text"
                label="Access Key for Crew"
                name="accessKey"
                placeholder="MyShow123"
                error={!!errors.accessKey}
                helperText={errors.accessKey?.message}
                fullWidth
                inputRef={register({ required: "Please enter an access key" })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                type="text"
                label="Access Key for Extras"
                name="accessKeyForExtras"
                placeholder="MyShow123EXTRA"
                error={!!errors.accessKeyForExtras}
                helperText={errors.accessKeyForExtras?.message}
                fullWidth
                inputRef={register({ required: "Please enter an access key for Extras" })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                // className={classes.root}
                variant="outlined"
                type="datetime-local"
                name="accessKeyForExtrasExpiry"
                label="Extras Key Expiration"
                error={!!errors.accessKeyForExtrasExpiry}
                helperText={errors.accessKeyForExtrasExpiry?.message}
                fullWidth
                inputRef={register({
                  required: "Please enter an expiration for the extra's key",
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
            <TextField
            variant="outlined"
            fullWidth
            label="Phone"
            name="phone"
            id="phone"
            error={!!errors.phone}
            helperText={errors.phone?.message}
            InputProps={{
              inputComponent: PhoneMask,
            }}
            InputLabelProps={{
              shrink: true,
            }}
            inputRef={register({
              required: "Please enter a phone number",
              setValueAs: value => value.replace(/\D/g, ""), // strip mask before validation
              validate: (value) => {
                // Remove mask characters and placeholders
                const digits = value.replace(/\D/g, "");
                if (digits.length < 10) {
                  return "Please enter a valid 10-digit phone number";
                }
                return true;
              },
            })}
            />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="outlined"
                type="email"
                label="Email"
                name="email"
                placeholder="user@example.com"
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                disabled
                inputRef={register({ required: "Please enter your email" })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box mb={3}>
                COPY & PASTE THESE INSTRUCTIONS TO SEND YOUR CREW:
                <p>
                  This Production is powered by showNAV, the new standard for production
                  navigation and logistics.
                  <p>Follow these simple steps and you’ll be ready to go!</p>
                  <ol>
                    <li>Download showNAV from your mobile App Store. It’s free to all app users!</li>
                    <li>Create your username and password.</li>
                    <li>Search for your production: "{productionNameValue.toUpperCase()}"</li>
                    <li>Enter the access key: "{accessKeyValue}"</li>
                  </ol>
                </p>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={pending || !isModified}
                fullWidth
              >
                {!pending ? "Save" : <CircularProgress size={28} />}
              </Button>
            </Grid>
            {submitError && <div><Alert severity="error" >{submitError.message}</Alert></div>}

          </Grid>
        </form>
      )}
    </>
  );
}

export default SettingsGeneral;
